import React, { useState } from 'react';
import { Form, Input, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { JobExtensionField } from '../../../types';
import { updateJobExtensionAnswer } from '../../../api/jobs';

const FieldsContainer = styled.div`
  margin-bottom: 16px;
`;

const FieldContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 4px;
  background-color: #f7f7f7;
`;

const FieldLabel = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const PrimaryButton = styled(Button)`
  &.ant-btn-primary {
    background-color: ${({ theme }) => theme.primaryColor};
    border-color: ${({ theme }) => theme.primaryColor};
  }
`;

const OutlineButton = styled(Button)`
  margin-right: 8px;
  border: 1px solid ${({ theme }) => theme.primaryColor};
  color: ${({ theme }) => theme.primaryColor};
`;

interface ExtensionFieldsSectionProps {
    fields: JobExtensionField[];
    jobId: string;
    onUpdate?: () => void;
    permissions: {
        canAnswer: boolean;
        canEdit: boolean;
    };
}

const ExtensionFieldsSection: React.FC<ExtensionFieldsSectionProps> = ({
    fields,
    jobId,
    onUpdate,
    permissions,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setIsSubmitting(true);

            const promises = Object.keys(values).map(async (key) => {
                const fieldId = key.split('_')[1];
                const value = values[key];
                return await updateJobExtensionAnswer(jobId, fieldId, value);
            });

            await Promise.all(promises);

            message.success(t('ANSWERS_SAVED'));
            setIsEditing(false);
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            message.error(t('FAILED_TO_SAVE_ANSWERS'));
            console.error('Failed to save answers', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = () => {
        const initialValues: Record<string, string | number | null> = {};
        fields.forEach((field) => {
            initialValues[`field_${field.id}`] = field.value;
        });
        form.setFieldsValue(initialValues);
        setIsEditing(true);
    };

    if (fields.length === 0) {
        return <div>{t('NO_EXTENSION_FIELDS')}</div>;
    }

    if (!isEditing) {
        return (
            <FieldsContainer>
                {fields.map((field) => (
                    <FieldContainer key={field.id}>
                        <FieldLabel>{field.name}</FieldLabel>
                        <div>
                            {field.value || '-'}
                        </div>
                    </FieldContainer>
                ))}
                {permissions.canAnswer && (
                    <ButtonsContainer>
                        <PrimaryButton
                            type="primary"
                            onClick={handleEdit}
                        >
                            {t('ANSWER_QUESTIONS')}
                        </PrimaryButton>
                    </ButtonsContainer>
                )}
            </FieldsContainer>
        );
    }

    return (
        <Form form={form} onFinish={handleSubmit} layout="vertical">
            {fields.map((field) => (
                <Form.Item
                    key={field.id}
                    label={field.name}
                    name={`field_${field.id}`}
                >
                    <Input.TextArea rows={4} placeholder={t('ENTER_ANSWER')} />
                </Form.Item>
            ))}
            <ButtonsContainer>
                <OutlineButton
                    onClick={() => setIsEditing(false)}
                >
                    {t('CANCEL')}
                </OutlineButton>
                <PrimaryButton
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                >
                    {t('SAVE')}
                </PrimaryButton>
            </ButtonsContainer>
        </Form>
    );
};

export default ExtensionFieldsSection; 